import api from './api';

export interface Bookmark {
    id: number;
    note: string | null;
    createdAt: string;
    course: {
        id: number;
        title: string;
        slug: string;
        description: string | null;
        thumbnailUrl: string | null;
        difficulty: string;
        estimatedDuration: number | null;
    };
}

export interface BookmarkListResponse {
    bookmarks: Bookmark[];
    count: number;
}

export interface BookmarkCheckResponse {
    isBookmarked: boolean;
    courseId: number;
}

export interface BookmarkToggleResponse {
    success: boolean;
    action: 'added' | 'removed';
    isBookmarked: boolean;
    bookmark?: Bookmark;
}

class BookmarkService {
    /**
     * Get all bookmarks for the current user
     */
    async getBookmarks(): Promise<Bookmark[]> {
        const response = await api.get<BookmarkListResponse>('/bookmarks');
        return response.data.bookmarks;
    }

    /**
     * Check if a course is bookmarked
     */
    async isBookmarked(courseId: number): Promise<boolean> {
        const response = await api.get<BookmarkCheckResponse>(`/bookmarks/check/${courseId}`);
        return response.data.isBookmarked;
    }

    /**
     * Toggle bookmark for a course
     */
    async toggleBookmark(courseId: number, note?: string): Promise<BookmarkToggleResponse> {
        const response = await api.post<BookmarkToggleResponse>(`/bookmarks/toggle/${courseId}`, {
            note: note || null
        });
        return response.data;
    }

    /**
     * Update bookmark note
     */
    async updateNote(bookmarkId: number, note: string): Promise<Bookmark> {
        const response = await api.patch<{ success: boolean; bookmark: Bookmark }>(`/bookmarks/${bookmarkId}`, {
            note
        });
        return response.data.bookmark;
    }

    /**
     * Delete a bookmark
     */
    async deleteBookmark(bookmarkId: number): Promise<void> {
        await api.delete(`/bookmarks/${bookmarkId}`);
    }
}

export const bookmarkService = new BookmarkService();
export default bookmarkService;
